import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';

import Breadcrumb from '../sec_breadcrumb/component';
import Typeset from '../base_typeset/component';

import Map from '../ui_store-map/component';
import SearchBlock from '../ui_store-search/component';
import Context from '@context/store-finder';
import Restapi from '@utils/api';
import Icon from '../base_svg-icon/component';
import { getDevice } from '@utils';
import Text from '@text';
import { t } from '@utils/i18n';

import './styles/styles.scss';

class StoreFinder extends Component {
  constructor(props) {
    super(props);

    this.state = {
      height: 500,
      listView: false,
      stores: [],
      filteredStores: [],
      activeStore: {},
      initiated: false,
      showMap: false,
      tempStoreId: null,
      googleLoaded: false,
      allStores: [],
      searching: false,
      reloadMap: () => {},
      searchMethod: () => {},
      showNoResults: () => {},
      clearSearch: () => {},
      toggleOpenMethod: () => {},
    };

    this.headerRef = createRef();
    this.restapi = new Restapi();
  }

  componentDidMount() {
    this.fetchStores();

    window.addEventListener('resize', this.setMapHeight);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setMapHeight);
  }


  /**
   * Fetch all stores from API if they're not already provided as a prop
   */
  fetchStores = () => {
    const { stores = [] } = this.props;
    if (stores.length) {
      const visibleStores = this.visibleStores(stores);
      return this.setState({
        stores: visibleStores,
        filteredStores: visibleStores,
        showMap: true,
      }, this.setMapHeight);
    }

    this.restapi.getAllStores()
      .then((response) => {
        const visibleStores = this.visibleStores(response);
        this.setState({
          stores: visibleStores,
          filteredStores: visibleStores,
          showMap: true,
        }, this.setMapHeight);
      });
  };

  /**
   * Set dynamic height of map to window height minus
   * the height of the header and the breadcrumbs and title
   */
  setMapHeight = () => {
    const minHeight = 500;
    const content = document.querySelector('.page-content');
    const contentOffset = content ? content.offsetTop : 200;
    const height = window.innerHeight - (contentOffset + this.headerRef?.current?.clientHeight);

    this.setState({
      height: (height > minHeight) ? height : minHeight,
    });
  };

  get breadcrumbs() {
    return [
      { name: t('common.home.link'), url: '/' },
      { name: t('storefinder.main.shortTitle') },
    ];
  }

  /* Context funtions */
  setStores = (stores, initiated = true, callback = () => {}) => {
    this.setState({ stores: this.visibleStores(stores), initiated }, callback);
  };

  setFilteredStores = (filteredStores, initiated = true, callback = () => {}) => {
    this.setState({ filteredStores: this.visibleStores(filteredStores), initiated }, callback);
  };

  setActiveStore = (activeStore, initiated = true, callback = () => {}) => {
    this.setState({ activeStore, initiated }, callback);
  };

  setReloadMapMethod = (reloadMap, callback = () => {}) => {
    this.setState({ reloadMap }, callback);
  };

  setSearchMethod = (searchMethod, callback = () => {}) => {
    this.setState({ searchMethod }, callback);
  };

  setNoResultsMethod = (showNoResults, callback = () => {}) => {
    this.setState({ showNoResults }, callback);
  };

  setClearSearchMethod = (clearSearch, callback = () => {}) => {
    this.setState({ clearSearch }, callback);
  };

  setTempStoreId = (tempStoreId, callback = () => {}) => {
    this.setState({ tempStoreId }, callback);
  };

  setToggleOpenMethod = (toggleOpenMethod, callback = () => {}) => {
    this.setState({ toggleOpenMethod }, callback);
  };

  setSearching = (searching, callback = () => {}) => {
    this.setState({ searching }, callback);
  };

  setAllStores = (allStores, callback = () => {}) => {
    this.setState({ allStores }, callback);
  };

  setGoogleLoaded = (googleLoaded, callback = () => {}) => {
    this.setState({ googleLoaded }, callback);
  };

  /**
   * Toggle from list to map view
   * Reset all icons and remve any active stores
   */
  toggleView = () => {
    this.state.activeStore.resetIcon?.();
    this.setState(prevState => ({
      listView: !prevState.listView,
      activeStore: {},
    }));
  };

  /**
   * Online return stores that are actually available. When selectMode is set only show
   * stores that are available for store pickup, based on the 'orderPickupHalted' attr.
   * @returns {array}
   */
  visibleStores(stores = []) {
    const { selectMode } = this.props;
    return selectMode ? stores.filter(store => !store.orderPickupHalted) : stores;
  }

  get contextValue() {
    const {
      listView,
      filteredStores,
      stores,
      activeStore,
      initiated,
      reloadMap,
      searchMethod,
      showNoResults,
      clearSearch,
      tempStoreId,
      toggleOpenMethod,
      googleLoaded,
      allStores,
      searching,
    } = this.state;
    const { selectMode, onSelect = () => {} } = this.props;
    return {
      initiated,
      listView,
      stores: this.visibleStores(stores),
      filteredStores: this.visibleStores(filteredStores),
      activeStore,
      reloadMap,
      searchMethod,
      showNoResults,
      clearSearch,
      tempStoreId,
      toggleOpenMethod,
      selectMode,
      onSelect,
      googleLoaded,
      allStores,
      searching,
      toggleView: this.toggleView,
      setStores: this.setStores,
      setFilteredStores: this.setFilteredStores,
      setActiveStore: this.setActiveStore,
      setReloadMapMethod: this.setReloadMapMethod,
      setSearchMethod: this.setSearchMethod,
      setNoResultsMethod: this.setNoResultsMethod,
      setClearSearchMethod: this.setClearSearchMethod,
      setTempStoreId: this.setTempStoreId,
      setToggleOpenMethod: this.setToggleOpenMethod,
      setGoogleLoaded: this.setGoogleLoaded,
      setAllStores: this.setAllStores,
      setSearching: this.setSearching,
    };
  }

  render() {
    const { store, onClose = () => {} } = this.props;
    const { showMap, height } = this.state;
    const { selectMode } = this.contextValue;
    const { isMobile } = getDevice();
    const className = this.props.modal ? 'store-finder-modal' : 'container';
    const altHeight = selectMode ? '100%' : `${height}px`;

    return (
      <div className={className}>
        {selectMode && <div onClick={onClose} className="store-finder-curtain"/>}
        <Context.Provider value={this.contextValue}>
          <div className="store-finder" data-test-id="component-storeFinder">
            {!selectMode && (
              <div className="map-header" ref={this.headerRef}>
                <Breadcrumb items={this.breadcrumbs} />
                <Typeset tag="h1" scale="2" scaleMd="3">
                  <Text>storefinder.main.title</Text>
                </Typeset>
              </div>
            )}

            <div className="map-wrapper" style={{ height: isMobile ? 'auto' : altHeight }}>
              {(showMap && !isMobile) && (<Map height={height}/>)}
              <span>
                <SearchBlock height={height} showMap={showMap} panel store={store}/>
              </span>
              {selectMode && (
                <div onClick={onClose} className="store-finder-close">
                  <Icon name="ico-error"/>
                </div>
              )}
            </div>
          </div>
        </Context.Provider>
      </div>

    );
  }
}

StoreFinder.propTypes = {
  store: PropTypes.object.isRequired,
  modal: PropTypes.bool,
  onSelect: PropTypes.func,
  onClose: PropTypes.func,
  selectMode: PropTypes.bool,
  stores: PropTypes.array,
};

export default StoreFinder;
